/**
 * TypeScript Types für Content Blocks
 * 
 * Definiert Typen für Content Blocks und deren Verwaltung.
 */

// =============================================================================
// SCOPE TYPES
// =============================================================================

export type ScopeType = 'product' | 'channel_package' | 'global_preset'

// Basis Block Types (ohne language suffix)
export type BaseBlockType =
  | 'title'
  | 'description'
  | 'short_description'
  | 'bullets'
  | 'seo_meta'

// Unterstützte Sprachen (JTL ISO 639-2)
export type LanguageCode = 'ger' | 'eng' | 'fre' | 'spa' | 'ita'

// Block Type mit MULTISPRACH-SUPPORT
// Kann sein: 'title' ODER 'title_ger' ODER 'title_eng' etc.
export type BlockType =
  | BaseBlockType
  | `${BaseBlockType}_${LanguageCode}`

// =============================================================================
// CONTENT BLOCK
// =============================================================================

export interface ContentBlock {
  id: string
  workspace_id: string
  scope_type: ScopeType
  scope_key: string  // UUID: Product ID, Channel Package ID, or NIL UUID for global
  block_type: BlockType
  current_version_id: string | null
  created_at: string
  updated_at: string
  // Joined data
  current_version?: ContentBlockVersionSummary
}

export interface ContentBlockVersionSummary {
  id: string
  version_number: number
  content: ContentBlockContent
  status: 'draft' | 'published' | 'archived'
  diff_summary: string | null
  source_type?: 'user_edit' | 'manual_publish' | 'scheduled_restore' | 'rollback' | 'regenerate'
  changed_by?: string  // User ID
  changed_by_name?: string
  created_at: string
}

// =============================================================================
// CONTENT BLOCK CONTENT (varies by block_type)
// =============================================================================

export type ContentBlockContent = 
  | string                    // title, description, short_description
  | string[]                  // bullets
  | SeoMetaContent            // seo_meta

export interface SeoMetaContent {
  title: string
  description: string
  keywords?: string[]
}

// =============================================================================
// CONTENT BLOCK WITH ALL DATA
// =============================================================================

export interface ContentBlockWithVersions extends ContentBlock {
  versions: ContentBlockVersionSummary[]
  active_schedule?: {
    id: string
    version_id: string
    publish_start_at: string
    publish_end_at: string
    status: 'scheduled' | 'active'
  } | null
}

// =============================================================================
// API RESPONSES
// =============================================================================

export interface ContentBlocksResponse {
  success: boolean
  content_blocks: ContentBlock[]
  error?: string
}

export interface ContentBlockResponse {
  success: boolean
  content_block: ContentBlockWithVersions
  error?: string
}

export interface ContentBlockCreateResponse {
  success: boolean
  content_block: ContentBlock
  version: ContentBlockVersionSummary
  message?: string
  error?: string
}

export interface ContentBlockUpdateResponse {
  success: boolean
  content_block: ContentBlock
  version: ContentBlockVersionSummary
  message?: string
  error?: string
}

// =============================================================================
// API REQUESTS
// =============================================================================

export interface CreateContentBlockRequest {
  scope_type: ScopeType
  scope_key: string
  block_type: BlockType
  content: ContentBlockContent
}

export interface UpdateContentBlockRequest {
  content: ContentBlockContent
  /** true = direkt veröffentlichen (Standard), false = nur Entwurf speichern */
  publish?: boolean
}

// =============================================================================
// QUERY PARAMS
// =============================================================================

export interface ContentBlocksQuery {
  scope_type?: ScopeType
  scope_key?: string
  block_type?: BlockType
}


// =============================================================================
// HELPERS
// =============================================================================

/**
 * Parse block_type mit language suffix.
 *
 * Beispiele:
 * - 'title' → { baseType: 'title', language: undefined }
 * - 'title_ger' → { baseType: 'title', language: 'ger' }
 * - 'description_eng' → { baseType: 'description', language: 'eng' }
 */
export function parseBlockType(blockType: BlockType): {
  baseType: BaseBlockType
  language?: LanguageCode
} {
  // Check if has language suffix (ends with _xxx where xxx is language code)
  const match = blockType.match(/^(.+)_(ger|eng|fre|spa|ita)$/)

  if (match) {
    return {
      baseType: match[1] as BaseBlockType,
      language: match[2] as LanguageCode
    }
  }

  // No language suffix - return as base type
  return {
    baseType: blockType as BaseBlockType,
    language: undefined
  }
}

/**
 * Build block_type mit language suffix.
 *
 * Beispiele:
 * - ('title', 'ger') → 'title_ger'
 * - ('description', undefined) → 'description'
 */
export function buildBlockType(
  baseType: BaseBlockType,
  language?: LanguageCode
): BlockType {
  if (language) {
    return `${baseType}_${language}` as BlockType
  }
  return baseType
}

/**
 * Gruppiere Content Blocks nach base type und language.
 *
 * Beispiel Input:
 * [
 *   { block_type: 'title_ger', ... },
 *   { block_type: 'title_eng', ... },
 *   { block_type: 'description_ger', ... }
 * ]
 *
 * Beispiel Output:
 * {
 *   'title': {
 *     'ger': { block_type: 'title_ger', ... },
 *     'eng': { block_type: 'title_eng', ... }
 *   },
 *   'description': {
 *     'ger': { block_type: 'description_ger', ... }
 *   }
 * }
 */
export function groupBlocksByLanguage(
  blocks: ContentBlock[]
): Record<BaseBlockType, Record<string, ContentBlock>> {
  const grouped: Record<string, Record<string, ContentBlock>> = {}

  for (const block of blocks) {
    const { baseType, language } = parseBlockType(block.block_type)
    const lang = language || 'default'

    if (!grouped[baseType]) {
      grouped[baseType] = {}
    }

    grouped[baseType][lang] = block
  }

  return grouped as Record<BaseBlockType, Record<string, ContentBlock>>
}

export function getSourceTypeLabel(sourceType?: string): string {
  if (!sourceType) return 'Unbekannt'

  const labels: Record<string, string> = {
    user_edit: 'Manuell bearbeitet',
    manual_publish: 'Manuell veröffentlicht',
    scheduled_restore: 'Geplant wiederhergestellt',
    rollback: 'Zurückgesetzt',
    regenerate: 'Aus Integration regeneriert',
    jtl_sync: 'Von JTL synchronisiert',
  }
  return labels[sourceType] || sourceType
}

export function getSourceTypeIcon(sourceType?: string): string {
  if (!sourceType) return '❓'

  const icons: Record<string, string> = {
    user_edit: '✏️',
    manual_publish: '📅',
    scheduled_restore: '⏰',
    rollback: '↩️',
    regenerate: '🔄',
    jtl_sync: '🔄',
  }
  return icons[sourceType] || '📝'
}
